'use client';

import ActiveIcon from '@/assets/svgs/statuses/active.svg';
import AwaitingIcon from '@/assets/svgs/statuses/awaiting.svg';
import CompleteIcon from '@/assets/svgs/statuses/complete.svg';
import DeferredIcon from '@/assets/svgs/statuses/deferred.svg';
import PausedIcon from '@/assets/svgs/statuses/paused.svg';
import PendingIcon from '@/assets/svgs/statuses/pending.svg';
import { inter500 } from '@/config/fonts';
import { Job } from '@/containers/Jobs/types';
import classNames from 'classnames';
import { HTMLAttributes, ReactNode } from 'react';

// Get status icon based on job status
const getJobStatusIcon = (status?: string) => {
  switch (status) {
    case 'active':
      return <ActiveIcon className="h-4 w-4" />;
    case 'paused':
      return <PausedIcon className="h-4 w-4" />;
    case 'pending':
      return <PendingIcon className="h-4 w-4" />;
    case 'complete':
      return <CompleteIcon className="h-4 w-4" />;
    case 'Out of Schedule':
      return <DeferredIcon className="h-4 w-4" />;
    case 'awaiting':
    case 'awaiting-check':
      return <AwaitingIcon className="h-4 w-4" />;
    default:
      return null;
  }
};

// Get status display text
const getStatusDisplayText = (status?: string) => {
  switch (status) {
    case 'active':
      return 'Active';
    case 'paused':
      return 'Paused';
    case 'pending':
      return 'Pending';
    case 'complete':
      return 'Complete';
    case 'Out of Schedule':
      return 'Out of Schedule';
    case 'awaiting':
    case 'awaiting-check':
      return 'Awaiting';
    default:
      return 'Unknown';
  }
};

interface JobItemViewProps extends HTMLAttributes<HTMLDivElement> {
  data: Job;
  actions?: ReactNode;
}

const JobItemView = ({ className, data, actions, style }: JobItemViewProps) => {
  // Use p2p_status if status is undefined, or fallback to 'pending'
  const jobStatus = data.status || data.p2p_status || 'pending';
  // Use p2p_name if name is undefined, or fallback to 'Untitled Job'
  const jobName = data.name || data.p2p_name || 'Untitled Job';
  // Use p2p_id if id is undefined, or fallback to 'No ID'
  const jobId = data.id || data.p2p_id || 'No ID';

  return (
    <div
      className={classNames('flex items-center justify-between', className)}
      style={style}>
      <div className="flex w-full gap-3">
        <div className="status-icon" title={getStatusDisplayText(jobStatus)}>
          <div className="dark:bg-base-300 text-gray-default dark:text-base-content flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gray-100">
            {getJobStatusIcon(jobStatus)}
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-0.5">
          <span className={classNames(inter500.className)}>{jobName}</span>
          <span
            className={classNames('text-gray-default text-[13px] dark:text-gray-400')}>
            {jobId}
          </span>
        </div>
        <div className="actions flex shrink-0 items-center gap-2">{actions}</div>
      </div>
    </div>
  );
};

export default JobItemView;
